import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageTier, PackageType, SetBuildingPackagePayload } from '@client/api';
import { Button, Typography } from '@client/toolkit';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const TIERS: PackageTier[] = ['Basic', 'Standard', 'Premium'];

const PACKAGE_CONFIGS: { type: PackageType; labelKey: string }[] = [
  { type: 'Portfolio', labelKey: 'portfolio' },
  { type: 'Management', labelKey: 'management' },
  { type: 'Eos', labelKey: 'eos' },
];

interface BuildingPackagesSectionProps {
  value: SetBuildingPackagePayload[];
  onChange: (packages: SetBuildingPackagePayload[]) => void;
}

export function BuildingPackagesSection({ value, onChange }: BuildingPackagesSectionProps) {
  const { t } = useTranslation('rentalObjects');

  const [expandedType, setExpandedType] = useState<PackageType | null>(null);
  const [pendingChange, setPendingChange] = useState<{
    type: PackageType;
    tier: PackageTier;
    action: 'enable' | 'changeTier';
  } | null>(null);

  const getPackage = useCallback(
    (type: PackageType) => value.find((p) => p.packageType === type),
    [value],
  );

  const isEnabled = useCallback((type: PackageType) => !!getPackage(type), [getPackage]);

  const getTier = useCallback(
    (type: PackageType) => getPackage(type)?.tier ?? 'Basic',
    [getPackage],
  );

  const getPackageName = useCallback(
    (type: PackageType) => {
      const config = PACKAGE_CONFIGS.find((c) => c.type === type);
      return config ? t(`buildingPackages.${config.labelKey}`) : type;
    },
    [t],
  );

  const getTierName = useCallback(
    (tier: PackageTier) => t(`buildingPackages.${tier.toLowerCase()}`),
    [t],
  );

  const applyChange = useCallback(
    (type: PackageType, tier: PackageTier, action: 'enable' | 'changeTier') => {
      if (action === 'enable') {
        onChange([...value, { packageType: type, tier }]);
      } else {
        onChange(value.map((p) => (p.packageType === type ? { ...p, tier } : p)));
      }
    },
    [value, onChange],
  );

  const handleToggle = useCallback(
    (type: PackageType) => {
      if (isEnabled(type)) {
        onChange(value.filter((p) => p.packageType !== type));
        if (expandedType === type) setExpandedType(null);
      } else {
        setPendingChange({ type, tier: 'Basic', action: 'enable' });
      }
    },
    [isEnabled, value, onChange, expandedType],
  );

  const handleTierSelect = useCallback(
    (type: PackageType, tier: PackageTier) => {
      if (tier === 'Premium') return;
      const currentTier = getTier(type);
      if (currentTier === tier) return;
      setPendingChange({ type, tier, action: 'changeTier' });
    },
    [getTier],
  );

  const handleConfirm = useCallback(() => {
    if (!pendingChange) return;
    applyChange(pendingChange.type, pendingChange.tier, pendingChange.action);
    setPendingChange(null);
  }, [pendingChange, applyChange]);

  const handleCancel = useCallback(() => {
    setPendingChange(null);
  }, []);

  return (
    <article className="pt-5 pb-8 px-4 border-t border-border space-y-4">
      <Typography variant="label" className="text-white">
        {t('buildingPackages.title')}
      </Typography>

      <div className="space-y-3">
        {PACKAGE_CONFIGS.map(({ type, labelKey }) => {
          const enabled = isEnabled(type);
          const tier = getTier(type);
          const expanded = expandedType === type;

          return (
            <div key={type} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Typography variant="label" className="text-white font-semibold">
                    {t(`buildingPackages.${labelKey}`)}
                  </Typography>
                  {enabled && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-400">
                        {t('buildingPackages.currentAccess')}:
                      </span>
                      <span className="text-xs font-medium bg-brand-primary text-black px-2 py-0.5 rounded">
                        {getTierName(tier)}
                      </span>
                    </div>
                  )}
                  {enabled && (
                    <button
                      type="button"
                      className="flex items-center gap-1 mt-2 text-sm text-brand-primary hover:text-brand-primary/80 transition-colors"
                      onClick={() => setExpandedType(expanded ? null : type)}
                    >
                      {expanded ? t('buildingPackages.close') : t('buildingPackages.change')}
                      {expanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  onClick={() => handleToggle(type)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    enabled ? 'bg-brand-primary' : ''
                  }`}
                  style={!enabled ? { backgroundColor: '#322f2c' } : undefined}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                      enabled ? 'translate-x-6 bg-black' : 'translate-x-1 bg-white'
                    }`}
                  />
                </button>
              </div>

              {enabled && expanded && (
                <div className="mt-4 space-y-2">
                  <span className="text-sm text-gray-400">{t('buildingPackages.selectTier')}</span>
                  {TIERS.map((tierOption) => {
                    const isPremium = tierOption === 'Premium';
                    const isSelected = tier === tierOption;

                    return (
                      <button
                        key={tierOption}
                        type="button"
                        disabled={isPremium}
                        onClick={() => handleTierSelect(type, tierOption)}
                        className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                          isPremium
                            ? 'border-border opacity-40 cursor-not-allowed'
                            : isSelected
                              ? 'border-brand-primary bg-white/5'
                              : 'border-border hover:border-gray-500 cursor-pointer'
                        }`}
                        title={isPremium ? t('buildingPackages.premiumTooltip') : undefined}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              isSelected ? 'border-brand-primary' : 'border-gray-500'
                            }`}
                          >
                            {isSelected && (
                              <div className="w-2 h-2 rounded-full bg-brand-primary" />
                            )}
                          </div>
                          <span className={`text-sm ${isPremium ? 'text-gray-500' : 'text-white'}`}>
                            {getTierName(tierOption)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {pendingChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={handleCancel}>
          <div className="fixed inset-0 bg-black/50" />
          <div
            className="relative z-10 bg-surface-base rounded-lg shadow-xl w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 pt-4 py-2">
              <h3 className="text-xl font-medium text-brand-primary">
                {t('buildingPackages.costWarningTitle')}
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-white">
                {t('buildingPackages.costWarning', {
                  packageName: getPackageName(pendingChange.type),
                  tier: getTierName(pendingChange.tier),
                })}
              </p>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3">
              <Button color="primary" size="sm" onClick={handleConfirm}>
                {t('buildingPackages.confirm')}
              </Button>
              <Button color="primary-dark" size="sm" onClick={handleCancel}>
                {t('buildingPackages.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
